import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import { FaChevronDown } from 'react-icons/fa';

export default function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  width = 'w-40',
  disabled = false,
  error = false,
  color = 'emerald', // 'emerald' or 'blue'
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return null;

    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = Math.min(options.length * 40, 192);

    if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
      return {
        position: 'fixed',
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: rect.width,
      };
    } else {
      return {
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      };
    }
  }, [options.length]);

  const handleOpen = () => {
    if (disabled) return;

    if (!isOpen) {
      // Calculate position BEFORE opening
      const style = calculatePosition();
      setDropdownStyle(style);
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen && buttonRef.current) {
        // Update dropdown position on scroll instead of closing it
        const style = calculatePosition();
        setDropdownStyle(style);
      }
    };

    const handleResize = () => {
      if (isOpen && buttonRef.current) {
        // Update dropdown position on resize
        const style = calculatePosition();
        setDropdownStyle(style);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen, calculatePosition]);

  const selectedOption = options.find(opt => opt.value === value);

  const handleSelect = optValue => {
    onChange(optValue);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${width}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        style={{ borderWidth: '1px' }}
        className={`w-full h-10 px-3 pr-10 border rounded-md bg-white text-left focus:outline-none flex items-center ${
          error
            ? 'border-red-500'
            : color === 'blue'
              ? `border-gray-300 focus:border-blue-500 ${isOpen ? 'border-blue-500' : ''}`
              : 'border-gray-300'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <span
          className={!value || value === 'all' || value === '' ? 'text-gray-500' : 'text-gray-900'}
        >
          {selectedOption?.label || placeholder}
        </span>
        <FaChevronDown
          className={`absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen &&
        dropdownStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="z-[99999] bg-white border-[1px] border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto animate-dropdown-in"
          >
            {options.map(opt => (
              <div
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={`w-full px-3 py-2 text-left flex items-center justify-between hover:bg-gray-50 cursor-pointer ${
                  value === opt.value
                    ? color === 'blue'
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-emerald-50 text-emerald-700'
                    : 'text-gray-700'
                }`}
              >
                <span>{opt.label}</span>
                {value === opt.value && (
                  <Check
                    className={`w-4 h-4 ${color === 'blue' ? 'text-blue-600' : 'text-emerald-600'}`}
                  />
                )}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
